import React, { useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';

import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import AgendaFilters from './components/AgendaFilters/AgendaFilters';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import LoaderVorstin from './components/svg/LoaderVorstin';

import AgendaCard from './components/AgendaContent/AgendaCard';
import {
  today,
  dbDateToTimestamp,
  dbDateToExactTimestamp,
  sortByTime,
  sortByPopularity,
  sortByRelease,
  sortAlphabetically,
  scrollToBegin,
  scrollToFromTime,
} from './utils/helpers';

const App = ({ activeSubSite, currentConfig, rootElement }) => {
  //state reflecting data and filtered data
  const [movieData, setMovieData] = useState(null); //source data that serves as input for each filtering
  const [fetching, setFetching] = useState(true); //loading state
  const [rendering, setRendering] = useState(true); //loading state
  const [filteredMovies, setFilteredMovies] = useState([]); //result of the filtered source data
  const [filteredCount, setFilteredCount] = useState(null);
  const [agendaCards, setAgendaCards] = useState([]); //state that holds the rendered components

  //state reflecting params that tweak the filtering
  const [activeTab, setActiveTab] = useState('now'); //'now' (nu te zien, default), 'kids', 'expected' or 'all'
  const [activeFilterDate, setActiveFilterDate] = useState(false); //boolean (false = 'alle dagen'; true = checkout activeTimeStamp)
  const [activeTimestamp, setActiveTimeStamp] = useState(today); //becomes a timestamp when date filtering is used (Only one, not an array)
  const [activeCinema, setActiveCinema] = useState(activeSubSite ? [activeSubSite] : []); //empty array or one or more cinema as filter
  const [activeTags, setActiveTags] = useState([]);
  const [fromTime, setFromTime] = useState(null); //in case of time sort and time filter this is a timestamp. If null it is sorted by popularity

  //params available in dropdown for filter -->
  const availableTabs = activeSubSite ? { now: 'Nu te zien' } : currentConfig.available_tabs; //for subsites only show 'now' tab
  const [availableCinema, setAvailableCinema] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableDatesMonths, setAvailableDatesMonths] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [SvgComponent] = useState(currentConfig.loading_svg);

  //**** FETCHING DATA:

  const fetchData = () => {
    const promise = new Promise((resolve, reject) => {
      let feedUrl;
      // currentConfig.multiLanguage && document.documentElement.lang === "en-EN" ? (feedUrl = "/en/fk-feed/agenda") : (feedUrl = "/fk-feed/agenda");
      feedUrl = '/?agenda_json';
      fetch(feedUrl)
        .then((response) => response.json())
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          console.error(error);
          reject(error);
        });
    });
    return promise;
  };

  //**** PARSING DATA:

  const parseMovies = (data) => {
    const dataCopy = JSON.parse(JSON.stringify(data));
    const allShowDays = [today];
    const allShowMonths = [];
    let daysCount = 1;
    let subsequentDay = new Date();
    while (daysCount <= currentConfig.minimum_days_in_filter) {
      subsequentDay.setDate(subsequentDay.getDate() + 1);
      allShowDays.push(+new Date(subsequentDay.getFullYear(), subsequentDay.getMonth(), subsequentDay.getDate()));
      daysCount++;
    }
    const allCinemas = [];
    const allTags = [];
    const parsedData = [];

    Object.values(dataCopy).forEach((movieCopy) => {
      if ('times' in movieCopy && movieCopy.times.length > 0) {
        movieCopy.times.map((show) => {
			 if ( show.location ) {
				let bioscoop = show.location;
				if ( show.location.indexOf(' ') >= 0 ) {
          		bioscoop = show.location.split(' ')[0];
				}
          	allCinemas.indexOf(bioscoop) === -1 && allCinemas.push(bioscoop);
			 }
          const timestamp = dbDateToTimestamp(show.program_start);
          allShowDays.indexOf(timestamp) === -1 && allShowDays.push(timestamp);
          show.date = timestamp;
          const timestampMonth = dbDateToExactTimestamp(`${show.program_start.slice(0, 4)}${show.program_start.slice(4, 6) + 1}000000`);
          allShowMonths.indexOf(timestampMonth) === -1 && allShowMonths.push(timestampMonth);
          return show;
        });
      }
      if ('tags' in movieCopy && movieCopy.tags.length !== 0) {
        Object.values(movieCopy.tags).map((tag) => {
          allTags.indexOf(tag) === -1 && allTags.push(tag);
        });
      }
      parsedData.push(movieCopy);
    });

    setAvailableCinema(allCinemas);
    setAvailableDates(allShowDays.sort((a, b) => a - b));
    setAvailableDatesMonths(allShowMonths.sort((a, b) => a - b));
    setAvailableTags(allTags.sort((a, b) => a.localeCompare(b)));

    return parsedData;
  };

  //*** FILTERING/MUTATION STATE FUNCTIONS:

  const updateSearchLinks = () => {
    const urlParams = new URLSearchParams();
    urlParams.set('f-tab', encodeURI(activeTab));
    const activeDay = activeFilterDate ? activeTimestamp : 'all';
    urlParams.set('f-day', encodeURI(activeDay));
    if (activeCinema.length > 0) {
      urlParams.set('f-cinema', encodeURI(activeCinema.join('+')));
    }
    if (activeTags.length > 0) {
      urlParams.set('f-tag', encodeURI(activeTags.join('+')));
    }
    if (fromTime) urlParams.set('f-sort', encodeURI(fromTime));
   //  history.replaceState(null, null, '?' + urlParams.toString());
  };

  const clickTabBar = (newTab) => {
    if (newTab === 'expected') {
      setActiveFilterDate(false);
      setActiveTimeStamp(null);
      setActiveCinema([]); //all cinema cause there is no cinema picker
      setActiveTags(['Verwacht']);
      setFromTime(null);
    } else if (newTab === 'all') {
      setActiveTags([]);
      setActiveFilterDate(false);
      setActiveTimeStamp(null);
      setActiveCinema([]); //all cinema cause there is no cinema picker
      setFromTime(null);
    } else if (newTab === 'kids') {
      if (!activeFilterDate) {
        //only do this when coming from an "alle dagen situation" like expected or all tabs
        setActiveFilterDate(true);
        setActiveTimeStamp(today);
      }
      setActiveTags(['Kids']);
    } else if (newTab === 'specials') {
      setActiveTags(['Special', 'Special no label']);
      setActiveFilterDate(false);
      setActiveTimeStamp(null);
      setFromTime(null);
    } else if (newTab === 'now') {
      if (!activeFilterDate) {
        setActiveFilterDate(true);
        setActiveTimeStamp(today);
      }
      if (activeTab !== 'all') setActiveTags([]); //reset tags when coming from kids, specials, expected
    }

    // setFromTime(null);
    setActiveTab(newTab);
  };

  const filterDate = (value) => {
    //receives value = 'all' or a timestamp date
    if (value === 'all') {
      setActiveFilterDate(false);
      setActiveTimeStamp(null);
    } else {
      setActiveFilterDate(true);
      setActiveTimeStamp(value);
    }
    scrollToBegin(rootElement);
  };

  const filterCinema = (value) => {
    //receives a value = name of cinema that is being added or removed
    if (activeCinema.includes(value)) {
      setActiveCinema([...activeCinema].filter((cinemaOption) => cinemaOption !== value));
    } else {
      setActiveCinema([...activeCinema].concat(value));
    }
    scrollToBegin(rootElement);
  };

  const filterTags = (value, onlyActivate) => {
    if (activeTags.includes(value)) {
      !onlyActivate && setActiveTags([...activeTags].filter((tagOption) => tagOption !== value));
    } else {
      setActiveTags([...activeTags].concat(value));
    }
    scrollToBegin(rootElement);
  };

  const resetFilters = () => {
    if (activeTab !== 'kids' && activeTab !== 'specials') setActiveTags([]);
    if (activeTab === 'kids' || activeTab === 'now') {
      setActiveFilterDate(true);
      setActiveTimeStamp(today);
      setActiveCinema(activeSubSite ? [activeSubSite] : []);
    }
    if (activeTab === 'specials') {
      setActiveCinema(activeSubSite ? [activeSubSite] : []);
    }
    setFromTime(null);
    scrollToBegin(rootElement);
  };

  const sortByUser = (choice) => {
    if (choice === 'popularity') {
      setFromTime(null);
      scrollToBegin(rootElement);
    } else if (choice === 'times') {
      setFromTime(today);
    }
  };

  const splitShows = (input) => {
    let res = [];
    input.forEach((movie) => {
      movie.times &&
        movie.times.forEach((show) => {
          const showCopy = JSON.parse(JSON.stringify(show));
          showCopy.timestamp = dbDateToExactTimestamp(showCopy.program_start);
          const movieCopy = JSON.parse(JSON.stringify(movie));
          movieCopy.times = [showCopy];
          res.push(movieCopy);
        });
    });
    return sortByTime(res);
  };

  const hasMatchingShowTags = (movie) => {
    if (movie.times && movie.times.length > 0) {
      let matchingShowTags = false;
      movie.times.forEach((show) => {
        show.tags &&
          show.tags.length > 0 &&
          show.tags.forEach((tag) => {
            if (!currentConfig.no_special_tags.includes(tag)) {
              matchingShowTags = true;
            }
          });
      });
      return matchingShowTags;
    }
    return false;
  };

  const filterAccordingToCurrentParams = (input) => {
    let copyInput = [...input];

    //filtering movies whether tag exists in the movie.tags object (all shows)
    let resultTagFilter = [];
    if (activeTags.length > 0) {
      resultTagFilter = [];
      copyInput.forEach((movie) => {
        let matchingTag = false;
        activeTags.forEach((tag) => {
          if (Object.values(movie.tags).indexOf(tag) > -1) matchingTag = true;
        });
        if (matchingTag) {
          resultTagFilter.push(movie);
        } else {
          if (activeTab === 'specials' && hasMatchingShowTags(movie)) {
            hasMatchingShowTags(movie) && resultTagFilter.push(movie);
          }
        }
      });
    } else {
      resultTagFilter = JSON.parse(JSON.stringify(copyInput));
    }

    //filtering movies based on date + filter times array for date(!)
    let resultFilterDate = [];
    if (activeFilterDate && activeTimestamp) {
      resultTagFilter.forEach((movie) => {
        const movieCopy = JSON.parse(JSON.stringify(movie));
        movieCopy.times = movieCopy.times.filter((show) => new Date(show.date).getMonth() === new Date(activeTimestamp).getMonth());
        resultFilterDate.push(movieCopy);
      });
    } else {
      resultFilterDate = JSON.parse(JSON.stringify(resultTagFilter)); //no filtering by date so we keep all
    }

    //filtering movies whether cinema exists in the location of the movie's shows + filter times array
    let resultFilterCinema = [];
    if (activeCinema.length > 0) {
      resultFilterDate.forEach((movie) => {
        const movieCopy = JSON.parse(JSON.stringify(movie));
        movieCopy.times = movieCopy.times.filter((show) => activeCinema.includes(show.location.split(' ')[0]));
        resultFilterCinema.push(movieCopy);
      });
    } else {
      resultFilterCinema = JSON.parse(JSON.stringify(resultFilterDate)); //no filtering by cinema so we keep all
    }

    return resultFilterCinema;
  };

  const switchLoadingSvg = () => {
    switch (SvgComponent) {
      case 'Camera':
        return <LoaderCamera />;
      case 'KinoLoader':
        return <LoaderKino />;
      case 'TheMoviesLoader':
        return <LoaderTheMovies />;
      case 'Vorstin':
        return <LoaderVorstin />;
      default:
        'Loading...';
    }
  };

  //*** USE-EFFECTS:

  //initialize app with data
  useEffect(() => {
    if (movieData) {
      return;
    }

    fetchData().then((data) => {
      setMovieData(parseMovies(data));
      setFetching(false);
    });
  }, []);

  //Filter (activated by a change in movieData or its parameters)
  useEffect(() => {
    if (!movieData) return;

    //filtering a copy of the source using current params from state; setFilteredMovies striggers a rerender of the following useEffect
    let filterDataObj = JSON.parse(JSON.stringify(movieData));
    if (activeTab === 'expected') {
      //no current params, only "expected"
      const expectedMovies = sortByRelease(
        filterDataObj.filter((movie) => {
          return movie.tags.hasOwnProperty('expected');
        })
      );
      setFilteredCount(expectedMovies.length);
      setFilteredMovies(expectedMovies);
    } else if (activeTab === 'specials') {
      let moviesFilterd;
      if (currentConfig.special_tab_filtering === 'only-with-times') {
        moviesFilterd = filterAccordingToCurrentParams(JSON.parse(JSON.stringify(movieData)).filter((movie) => movie['times'] && movie['times'].length > 0));
      } else if (currentConfig.special_tab_filtering === 'all-specials') {
        moviesFilterd = filterAccordingToCurrentParams(JSON.parse(JSON.stringify(movieData)));
      } else {
        moviesFilterd = filterAccordingToCurrentParams(JSON.parse(JSON.stringify(movieData))); //default in case of typo's config
      }
      let moviesSorted = [];
      if (currentConfig.special_tab_sorting === 'release-date') {
        moviesSorted = sortByRelease(moviesFilterd);
      } else if (currentConfig.special_tab_sorting === 'populair') {
        moviesSorted = sortByPopularity(moviesFilterd);
      } else {
        moviesSorted = sortByPopularity(moviesFilterd); //default in case of typo's config
      }
      setFilteredCount(moviesSorted.length);
      setFilteredMovies(moviesSorted);
    } else if (activeTab === 'all') {
      //include the movies without showtime, filter the params
      const allMovies = sortAlphabetically(filterAccordingToCurrentParams(JSON.parse(JSON.stringify(filterDataObj))));
      setFilteredCount(allMovies.length);
      setFilteredMovies(allMovies);
    } else {
      //exclude the movies without showtime (tabs 'now', 'kids'), filter the params

      const resultFiltering = filterAccordingToCurrentParams(sortByPopularity(filterDataObj.filter((movie) => movie['times'] && movie['times'].length > 0)));
      const moviesWithTimes = JSON.parse(JSON.stringify(resultFiltering)).filter((movie) => movie['times'] && movie['times'].length > 0);
      setFilteredCount(moviesWithTimes.length);

      if ((activeTab === 'now' || activeTab === 'kids') && fromTime) {
        setFilteredMovies(splitShows(moviesWithTimes));
      } else {
        setFilteredMovies(moviesWithTimes);
      }
      ScrollTrigger.refresh(true);
    }
  }, [movieData, activeTab, activeFilterDate, activeTimestamp, activeCinema, activeTags, fromTime]);

  //render content (listening to a change filteredMovies)
  useEffect(() => {
    setRendering(true);

    setAgendaCards(
      filteredMovies.map((movie, index) => (
        <AgendaCard
          movie={movie}
          key={movie.post_id + '-' + index}
          id={`${movie.post_id}-${movie.times && movie.times.length > 0 && movie.times[0].hasOwnProperty('timestamp') && movie.times[0].timestamp}`}
          activeFilterDate={activeFilterDate}
          activeTab={activeTab}
          activeTimestamp={activeTimestamp}
          filterTags={filterTags}
          currentConfig={currentConfig}
          fromTime={fromTime}
          animationDelay={{ '--animation-delay': `${(index < 4 ? index + 1 : 4) * 100}ms` }}
          rootElement={rootElement}
        />
      ))
    );
    setTimeout(() => {
      setRendering(false);
      //in case of sorting by time and date param is "Vandaag" --> future development: for each date param but then fromTime will be a default from horizontal scrollbar
      if (fromTime && activeFilterDate && activeTimestamp === today) {
        scrollToFromTime(rootElement, filteredMovies);
      }
    }, 200);
  }, [filteredMovies]);

  // update search linkgs on change of filter parameters
  useEffect(() => {
    if (!fetching) updateSearchLinks();
  }, [activeTab, activeFilterDate, activeTimestamp, activeCinema, activeTags, fromTime]);

  // preset filters from url when page loads
  useEffect(() => {
    if (movieData && location.search) {
      const queryParams = new URLSearchParams(window.location.search);
      let tab = '';

      if (queryParams.has('f-tab')) {
        const param = decodeURI(queryParams.get('f-tab'));
        tab = param;
        if (Object.keys(availableTabs).includes(param)) setActiveTab(param);
      }

      //tab overrules day params in some cases
      if (tab === 'expected' || tab === 'all' || tab === 'specials') {
        setActiveFilterDate(false);
        setActiveTimeStamp(null);
      } else {
        if (queryParams.has('f-day')) {
          const param = decodeURI(queryParams.get('f-day'));
          const paramParsed = Number(param);
          if (availableDates.includes(paramParsed)) setActiveTimeStamp(paramParsed);
          if (param === 'all') setActiveFilterDate(false);
        }
      }

      //tab overrules cinema params in some cases
      if (tab === 'expected' || tab === 'all') {
        setActiveCinema([]);
      } else {
        if (queryParams.has('f-cinema')) {
          let param = decodeURI(queryParams.get('f-cinema')).split('+');
          param = param.filter((cinema) => availableCinema.includes(cinema));
          if (param.length > 0) setActiveCinema(param);
        }
      }

      //tab overrules and sets tags
      if (tab === 'expected') {
        setActiveTags(['Verwacht']);
      } else if (tab === 'kids') {
        setActiveTags(['Kids']);
      } else if (tab === 'specials') {
        setActiveTags(['Special', 'Special no label']);
      } else {
        if (queryParams.has('f-tag')) {
          let param = decodeURI(queryParams.get('f-tag')).split('+');
          param = param.filter((tag) => availableTags.includes(tag) || tag === 'special' || tag === 'Kids');
          if (param.length > 0) setActiveTags(param);
        }
      }

      if (!tab && queryParams.has('f-sort')) {
        let param = decodeURI(queryParams.get('f-sort'));
        setFromTime(parseInt(param));
      }
    }
  }, [movieData]);

  if (fetching) return <div className="loader__container--agenda">{switchLoadingSvg()}</div>;

  return (
    <>

      {activeTab !== 'expected' && (
        <AgendaFilters
          activeTab={activeTab}
          activeSubSite={activeSubSite}
          filteredCount={filteredCount}
          activeFilterDate={activeFilterDate}
          activeTimestamp={activeTimestamp}
          activeCinema={activeCinema}
          activeTags={activeTags}
          sortingDecision={fromTime ? 'times' : 'popularity'}
          availableDates={availableDates}
          availableDatesMonths={availableDatesMonths}
          availableCinema={availableCinema}
          availableTags={availableTags}
          filterDate={filterDate}
          filterCinema={filterCinema}
          filterTags={filterTags}
          resetFilters={resetFilters}
          sortByUser={sortByUser}
          currentConfig={currentConfig}
          rootElement={rootElement}
        />
      )}
      {agendaCards.length <= 0 ? (
        <EmptyAgenda text={currentConfig.empty_agenda_text} />
      ) : (
        <ol
          className={`js_cards-container featured
						${!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'}`}
        >
          {agendaCards}
        </ol>
      )}
    </>
  );
};

export default hot(module)(App);
